import Tag from "@/components/Tag";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import ShareButton from "@/components/ShareButton";

const ExternalArrow = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden
    className=""
  >
    <path
      d="M1 11L11 1M11 1H3M11 1V9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @param {import("@prismicio/client").Content.ProjectDataSliceDefaultPrimaryInfosItem} info
 */
const InfoItem = ({ info }) => {
  if (!isFilled.keyText(info.title) && !isFilled.keyText(info.label)) {
    return null;
  }

  return (
    <div className="flex flex-col items-start">
      {isFilled.keyText(info.title) && (
        <p className="p-lg-n text-blanc">{info.title}</p>
      )}
      {isFilled.keyText(info.label) && (
        <p className="p-lg-n bd text-blanc">
          {info.label}
        </p>
      )}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectDataSlice} ProjectDataSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectDataSlice>} ProjectDataProps
 * @type {import("react").FC<ProjectDataProps>}
 */
const ProjectData = ({ slice }) => {
  const { sub_header, infos, title_tag, tags, cta } = slice.primary;

  const filledInfos = Array.isArray(infos)
    ? infos.filter(
      (info) => isFilled.keyText(info.title) || isFilled.keyText(info.label),
    )
    : [];

  const firstInfo = filledInfos.shift();
  const lastInfo = filledInfos.pop();
  const middleInfos = filledInfos;

  const filledTags = Array.isArray(tags)
    ? tags.filter((item) => isFilled.keyText(item.tag))
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="">
      <div className="w-full gap-10 flex flex-col">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-10 rounded-[20px] bg-bleu p-[30px]">

            <p className="p-base-n text-rouge uppercase">
              {sub_header}
            </p>

            <div className="flex flex-col gap-2.5 md:gap-[30px]">
              {filledInfos.length > 0 && (
                <div className="flex flex-col md:justify-between gap-5">
                  <InfoItem info={firstInfo} />
                  <div className="flex flex-row gap-10">
                    {middleInfos.map((info, index) => <InfoItem key={`info-${index}`} info={info} />)}
                  </div>
                  <InfoItem info={lastInfo} />
                </div>
              )}
              {(isFilled.keyText(title_tag) ||
                filledTags.length > 0 ||
                isFilled.link(cta)) && (
                  <div className="flex flex-col gap-5">
                    {isFilled.keyText(title_tag) && (
                      <p className="p-lg-n text-blanc">
                        {title_tag}
                      </p>
                    )}

                    <div className="flex flex-col justify-between gap-10">
                      {filledTags.length > 0 && (
                        <div className="flex flex-wrap gap-2.5 w-full">
                          {filledTags.map((item, index) => (
                            <Tag
                              key={`tag-${index}`}
                              color="bleu-50"
                              text={item.tag}
                            />
                          ))}
                        </div>
                      )}


                    </div>
                  </div>
                )}
            </div>
            {isFilled.link(cta) && (
                <PrismicNextLink prefetch={false}
                  field={cta}
                  className="inline-flex flex-row items-center gap-2.5 text-vert-50 w-fit">
                  <span className="p-base-n md">{cta.text}</span>
                  <ExternalArrow />
                </PrismicNextLink>
              )}
          </div>
          <ShareButton className="flex w-full cursor-pointer flex-row items-center justify-end gap-3 p-blog font-medium text-bleu">
            Partager le projet
            <img src="/icons/share.svg" alt="" width={20} height={20} aria-hidden />
          </ShareButton>
        </div>

        <div className="w-full hidden md:block">
          <PrismicNextLink field={slice.primary.cta_contact}>
            <div className="h4-n sbd p-5 bg-beige text-bleu rounded-[16px] flex flex-row items-center justify-between">
              {slice.primary.cta_contact.text}
              <img src="/icons/arrow-right.svg" alt="" width={20} height={20} aria-hidden />
            </div>
          </PrismicNextLink>
        </div>
      </div>
    </section >
  );
};

export default ProjectData;
